import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { adminService } from '../../services/admin.service';
import { LLMConfig, STTConfig, TTSConfig, ExternalAPIConfig } from '../../types/admin.types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

export const CredentialsConfig: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'llm' | 'stt' | 'tts' | 'api'>('llm');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const [llmConfigs, setLlmConfigs] = useState<LLMConfig[]>([]);
  const [sttConfigs, setSttConfigs] = useState<STTConfig[]>([]);
  const [ttsConfigs, setTtsConfigs] = useState<TTSConfig[]>([]);
  const [apiConfigs, setApiConfigs] = useState<ExternalAPIConfig[]>([]);

  const hasPerm = (perm: string) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    const explicit = Array.isArray((user as any).permissions) && (user as any).permissions.includes(perm);
    if (explicit) return true;
    // Fallback for legacy accounts where permission arrays are stale/incomplete.
    const role = String((user as any).role || '').toLowerCase();
    if (perm === 'credential.view') return ['owner', 'admin', 'editor', 'viewer'].includes(role);
    if (perm === 'credential.create' || perm === 'credential.edit') return ['owner', 'admin', 'editor'].includes(role);
    if (perm === 'credential.delete') return ['owner', 'admin'].includes(role);
    return false;
  };

  const canView = hasPerm('credential.view');
  const canCreate = hasPerm('credential.create');
  const canEdit = hasPerm('credential.edit');
  const canDelete = hasPerm('credential.delete');

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      if (!canView) {
        setError('Credential view permission required.');
        return;
      }
      const [llm, stt, tts, apis] = await Promise.all([
        adminService.getLLMConfigs(),
        adminService.getSTTConfigs(),
        adminService.getTTSConfigs(),
        adminService.getExternalAPIConfigs(),
      ]);
      setLlmConfigs(llm || []);
      setSttConfigs(stt || []);
      setTtsConfigs(tts || []);
      setApiConfigs(apis || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleSaveConfig = async (config: any) => {
    try {
      if (!canEdit && !canCreate) {
        enqueueSnackbar('Credential edit permission required.', { variant: 'error' });
        return;
      }
      if (!config?.type) {
        throw new Error('Invalid config type');
      }
      if (config.type === 'llm') {
        if (!config.provider) throw new Error('Provider is required');
        const payload: any = {
          is_active: config.is_active ?? true,
          model: config.model,
        };
        const k = String(config.api_key || '').trim();
        if (k && !k.includes('***')) payload.api_key = k;
        await adminService.updateLLMConfig(config.provider, payload);
      } else if (config.type === 'stt') {
        if (!config.provider) throw new Error('Provider is required');
        const payload: any = {
          is_active: config.is_active ?? true,
          model: config.model,
          language: config.language,
        };
        const k = String(config.api_key || '').trim();
        if (k && !k.includes('***')) payload.api_key = k;
        await adminService.updateSTTConfig(config.provider, payload);
      } else if (config.type === 'tts') {
        if (!config.provider) throw new Error('Provider is required');
        const payload: any = {
          is_active: config.is_active ?? true,
          model: config.model,
          voice: config.voice,
        };
        const k = String(config.api_key || '').trim();
        if (k && !k.includes('***')) payload.api_key = k;
        await adminService.updateTTSConfig(config.provider, payload);
      } else if (config.type === 'api') {
        // If it already exists, update; otherwise create
        const exists = apiConfigs.some((c) => c.name === config.name);
        const payload: any = {
          name: config.name,
          base_url: config.base_url,
          auth_type: config.auth_type,
          is_active: config.is_active ?? true,
        };
        const k = String(config.api_key || '').trim();
        if (k && !k.includes('***')) payload.api_key = k;
        if (exists) {
          await adminService.updateExternalAPIConfig(config.name, payload);
        } else {
          await adminService.createExternalAPIConfig(payload);
        }
      }
      setConfigDialogOpen(false);
      setEditingConfig(null);
      enqueueSnackbar('Configuration saved', { variant: 'success' });
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save configuration');
    }
  };

  const handleDeleteApiConfig = async (name: string) => {
    try {
      if (!canDelete) {
        enqueueSnackbar('Credential delete permission required.', { variant: 'error' });
        return;
      }
      await adminService.deleteExternalAPIConfig(name);
      enqueueSnackbar('External API deleted', { variant: 'success' });
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete configuration');
    }
  };

  const handleTestLlmKey = async () => {
    try {
      setError(null);
      if (!editingConfig?.provider) {
        enqueueSnackbar('Select a provider first', { variant: 'warning' });
        return;
      }
      setTesting(true);
      const res = await adminService.testLLMConfig(
        editingConfig.provider,
        (editingConfig.api_key || '').trim() || undefined
      );
      if (res?.ok) {
        enqueueSnackbar(res.message || 'LLM key works', { variant: 'success' });
      } else {
        enqueueSnackbar(res?.error || 'LLM test failed', { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || err?.message || 'LLM test failed', { variant: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const renderConfigCard = (config: any, type: string) => {
    return (
      <Card key={config.name || config.provider} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                {config.name || config.provider}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {config.model || config.language || config.voice || config.base_url || 'Not configured'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={config.is_active ? 'Active' : 'Inactive'}
                color={config.is_active ? 'success' : 'default'}
                size="small"
              />
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  // Do not prefill api_key with masked value; user can paste a new one if they want to rotate it.
                  setEditingConfig({ ...config, api_key: '', _api_key_saved: config.api_key, type });
                  setConfigDialogOpen(true);
                }}
                disabled={!canEdit}
              >
                Edit
              </Button>
              {type === 'api' && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeleteApiConfig(config.name)}
                  disabled={!canDelete}
                >
                  Delete
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Credential view permission required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Credentials & API Configuration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Button onClick={() => setActiveTab('llm')} sx={{ mr: 1 }} variant={activeTab === 'llm' ? 'contained' : 'outlined'}>
          LLM
        </Button>
        <Button onClick={() => setActiveTab('stt')} sx={{ mr: 1 }} variant={activeTab === 'stt' ? 'contained' : 'outlined'}>
          STT
        </Button>
        <Button onClick={() => setActiveTab('tts')} sx={{ mr: 1 }} variant={activeTab === 'tts' ? 'contained' : 'outlined'}>
          TTS
        </Button>
        <Button onClick={() => setActiveTab('api')} variant={activeTab === 'api' ? 'contained' : 'outlined'}>
          External APIs
        </Button>
      </Box>

      <Grid container spacing={3}>
        {activeTab === 'llm' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Large Language Model (LLM) Configuration
              </Typography>
              {llmConfigs.map((config) => renderConfigCard(config, 'llm'))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingConfig({ type: 'llm', provider: 'openai', is_active: true });
                  setConfigDialogOpen(true);
                }}
                disabled={!canCreate}
              >
                Add LLM Provider
              </Button>
            </Paper>
          </Grid>
        )}

        {activeTab === 'stt' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Speech-to-Text (STT) Configuration
              </Typography>
              {sttConfigs.map((config) => renderConfigCard(config, 'stt'))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingConfig({ type: 'stt', provider: 'openai', is_active: true });
                  setConfigDialogOpen(true);
                }}
                disabled={!canCreate}
              >
                Add STT Provider
              </Button>
            </Paper>
          </Grid>
        )}

        {activeTab === 'tts' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Text-to-Speech (TTS) Configuration
              </Typography>
              {ttsConfigs.map((config) => renderConfigCard(config, 'tts'))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingConfig({ type: 'tts', provider: 'openai', is_active: true });
                  setConfigDialogOpen(true);
                }}
                disabled={!canCreate}
              >
                Add TTS Provider
              </Button>
            </Paper>
          </Grid>
        )}

        {activeTab === 'api' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                External API Configuration
              </Typography>
              {apiConfigs.map((config) => renderConfigCard(config, 'api'))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingConfig({ type: 'api', is_active: true });
                  setConfigDialogOpen(true);
                }}
                disabled={!canCreate}
              >
                Add External API
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => {
          setConfigDialogOpen(false);
          setEditingConfig(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Configure {editingConfig?.type?.toUpperCase()} Provider
        </DialogTitle>
        <DialogContent>
          {editingConfig?.type === 'llm' && (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={editingConfig.provider || ''}
                  onChange={(e) => setEditingConfig({ ...editingConfig, provider: e.target.value })}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic</MenuItem>
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="azure">Azure</MenuItem>
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                label={`API Key${editingConfig?._api_key_saved ? ` (saved ${editingConfig._api_key_saved})` : ''}`}
                fullWidth
                type="password"
                value={editingConfig.api_key || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, api_key: e.target.value })}
                helperText="Leave blank to keep the currently saved key."
              />
              <TextField
                margin="dense"
                label="Model"
                fullWidth
                value={editingConfig.model || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, model: e.target.value })}
              />
            </>
          )}
          
          {editingConfig?.type === 'api' && (
            <>
              <TextField
                margin="dense"
                label="Name"
                fullWidth
                value={editingConfig.name || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, name: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Base URL"
                fullWidth
                value={editingConfig.base_url || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, base_url: e.target.value })}
              />
              <TextField
                margin="dense"
                label="API Key"
                fullWidth
                type="password"
                value={editingConfig.api_key || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, api_key: e.target.value })}
              />
            </>
          )}

          {editingConfig?.type === 'stt' && (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={editingConfig.provider || ''}
                  onChange={(e) => setEditingConfig({ ...editingConfig, provider: e.target.value })}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="azure">Azure</MenuItem>
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                label={`API Key${editingConfig?._api_key_saved ? ` (saved ${editingConfig._api_key_saved})` : ''}`}
                fullWidth
                type="password"
                value={editingConfig.api_key || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, api_key: e.target.value })}
                helperText="Leave blank to keep the currently saved key."
              />
              <TextField
                margin="dense"
                label="Model"
                fullWidth
                value={editingConfig.model || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, model: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Language (e.g. en, id, en-US)"
                fullWidth
                value={editingConfig.language || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, language: e.target.value })}
              />
            </>
          )}

          {editingConfig?.type === 'tts' && (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={editingConfig.provider || ''}
                  onChange={(e) => setEditingConfig({ ...editingConfig, provider: e.target.value })}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="azure">Azure</MenuItem>
                  <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                label={`API Key${editingConfig?._api_key_saved ? ` (saved ${editingConfig._api_key_saved})` : ''}`}
                fullWidth
                type="password"
                value={editingConfig.api_key || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, api_key: e.target.value })}
                helperText="Leave blank to keep the currently saved key."
              />
              <TextField
                margin="dense"
                label="Model"
                fullWidth
                value={editingConfig.model || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, model: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Voice"
                fullWidth
                value={editingConfig.voice || ''}
                onChange={(e) => setEditingConfig({ ...editingConfig, voice: e.target.value })}
              />
            </>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={editingConfig?.is_active ?? true}
                disabled={!editingConfig}
                onChange={(e) => {
                  if (!editingConfig) return;
                  setEditingConfig({ ...editingConfig, is_active: e.target.checked });
                }}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfigDialogOpen(false);
              setEditingConfig(null);
            }}
          >
            Cancel
          </Button>
          {editingConfig?.type === 'llm' && (
            <Button
              variant="outlined"
              onClick={handleTestLlmKey}
              disabled={testing || !editingConfig?.provider}
            >
              {testing ? 'Testing…' : 'Test key'}
            </Button>
          )}
          <Button onClick={() => handleSaveConfig(editingConfig)} variant="contained" disabled={!canEdit && !canCreate}>
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};